import { getHighestNumber } from '@/util/number';
import { useApplication } from '@/hooks/useApplication';
import { useUpdateNodes } from '@/hooks/useUpdateNodes';
import { findSubroutine } from '@/util/application';
import { getFolderLabels } from '@/util/folder';
import { isNumber } from '@/util/typeGuards';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { FC, useEffect, useState } from 'react';

type EditSubroutineDialogProps = {
  open: boolean;
  onClose: () => void;
  subroutineId?: number;
  setSelectedSubroutineId?: (newId: number) => void;
  folderId?: number;
  type?: 'new' | 'edit';
};

const EditSubroutineDialog: FC<EditSubroutineDialogProps> = (props) => {
  const { application, setApplication } = useApplication();
  const { setUpdateNodes } = useUpdateNodes();
  const [label, setLabel] = useState('');

  const saveSubroutine = (id: number, label: string, isNew?: boolean) => {
    isNew && props.setSelectedSubroutineId && props.setSelectedSubroutineId(id);
    setApplication({
      ...application,
      subroutines: isNew
        ? [
            ...application.subroutines,
            {
              id: id,
              startblock: undefined,
              args: [],
              arg_bindings: [],
              label: label,
              nodes: [],
              return_types: [],
              folder: props.folderId
            }
          ]
        : application.subroutines.map((subroutine) => {
            if (subroutine.id === id) {
              return {
                ...subroutine,
                label: label
              };
            } else {
              return subroutine;
            }
          })
    });

    const subNodes = application.subroutines.find((subroutine) => subroutine.id === id)?.nodes;
    !isNew && subNodes && setUpdateNodes(subNodes.map((node) => node.toString()));
    props.onClose();
  };

  const saveFolder = (id: number, label: string) => {
    setApplication({
      ...application,
      folders: application.folders?.map((folder) => {
        if (folder.id === id) {
          return { ...folder, label: label };
        } else {
          return folder;
        }
      })
    });
    props.onClose();
  };

  const handleAdd = () => {
    if (props.type === 'new') {
      const newId = getHighestNumber(application.subroutines, 'id');
      if (!isNumber(newId)) return;
      saveSubroutine(newId + 1, label, true);
    } else if (props.subroutineId) {
      saveSubroutine(props.subroutineId, label);
    } else {
      if (!props.folderId) return;
      saveFolder(props.folderId, label);
    }
  };

  const folderLabel = props.folderId && getFolderLabels(props.folderId, application);

  const dialogTitle = () => {
    const folderText = props.folderId ? ' under folder: ' + folderLabel : '';

    if (props.type === 'new') {
      return 'New Subroutine' + folderText;
    }

    if (props.subroutineId) {
      return 'Edit Subroutine ' + folderText;
    }

    return 'Edit Folder: ' + folderLabel;
  };

  useEffect(() => {
    if (props.open && props.type === 'edit') {
      props.subroutineId
        ? setLabel(findSubroutine(application.subroutines, props.subroutineId)?.label || '')
        : setLabel(application.folders?.find((folder) => folder.id === props.folderId)?.label || '');
    } else {
      setLabel('');
    }
  }, [props.open]);

  return (
    <Dialog open={props.open} onClose={props.onClose} disableRestoreFocus>
      <Box component='form' onSubmit={(e) => e.preventDefault()}>
        <DialogTitle>{dialogTitle()}</DialogTitle>
        <DialogContent>
          <TextField
            label='Label'
            value={label}
            autoFocus={props.open}
            onChange={(e) => setLabel(e.target.value)}
            sx={{ mt: 1 }}
            required
            fullWidth
            InputProps={{ autoComplete: 'off' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose} color='primary'>
            Cancel
          </Button>
          <Button variant='contained' color='primary' disabled={!label} onClick={handleAdd} type='submit'>
            {props.type === 'edit' ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default EditSubroutineDialog;
